import { useEffect, useRef, useState } from "react";
import { getOfflineStoreData, OfflineStores } from "../offline-db";
import { loadActiveBills, OfflineBill } from "../redux-store";
import { useAppDispatch, useAppSelector } from "../redux-store/hooks";
import { getCategoryList, getClientInfo, getFloorList, getPlaceList, getProductsList, getPropsList, getTableList } from "../services";
import { AppConfig } from "../config";
import { STATUS } from "../constants";


const useStartupLoader = () => {
    const [message, setMessage] = useState("loading...");
    const dispatcher = useAppDispatch();
    const inFlightLoads = useRef(new Set<string>());
    const retryCounts = useRef(new Map<string, number>());
    const products = useAppSelector((state) => state.allproducts.data);
    const productStatus = useAppSelector((state) => state.allproducts.status);
    const categories = useAppSelector((state) => state.categories.data);
    const categoryStatus = useAppSelector((state) => state.categories.status);
    const clientInfoStatus = useAppSelector((state) => state.clientInfo.status);
    const clientInfoError = useAppSelector((state) => state.clientInfo.error);
    const floors = useAppSelector((state) => state.preloadData.floors);
    const places = useAppSelector((state) => state.preloadData.places);
    const tables = useAppSelector((state) => state.preloadData.tables);
    const props = useAppSelector((state) => state.props.props);

    const loadActiveBillsfromOfflineDB = async() => {
        try {
            setMessage("loading offline active sessions...");
            const activeBills:OfflineBill[] = await getOfflineStoreData(OfflineStores.ActiveBills);
            const activeBillsObj:{[sessionId: string] : OfflineBill} = 
            activeBills.reduce((acc: {[sessionId: string] : OfflineBill}, val) => {
                acc[val.sessionId] = val; return acc
            }, {} );
            dispatcher(loadActiveBills(activeBillsObj));
        } catch(e) {
            console.error(e);
        }
    }
    

    const loadOfflineData = async() => {
        try {
            setMessage("loading offline data...");
            const isDbInitialized = sessionStorage.getItem('isDbReady');
            if(isDbInitialized) {
                await loadActiveBillsfromOfflineDB();
            }
        } catch(e) {
            console.error(e);
        }
    }


    const loadAll = async() => {
        try {
            //await loadOfflineData();
            setMessage(clientInfoStatus === STATUS.FAILED ? "retrying app data..." : "loading app data...");
            const requests: Promise<unknown>[] = [];

            const queueLoad = (
                key: string,
                isLoaded: boolean,
                request: () => Promise<unknown>,
                status?: string
            ) => {
                const retryCount = retryCounts.current.get(key) || 0;
                if (
                    isLoaded ||
                    status === STATUS.LOADING ||
                    status === STATUS.SUCCESS ||
                    inFlightLoads.current.has(key) ||
                    (status === STATUS.FAILED && retryCount >= 2)
                ) {
                    if (status === STATUS.FAILED && retryCount >= 2) {
                        setMessage(clientInfoError || "Unable to load app data. Please reload.");
                    }
                    return;
                }

                inFlightLoads.current.add(key);
                retryCounts.current.set(key, retryCount + 1);
                requests.push(
                    request().finally(() => {
                        inFlightLoads.current.delete(key);
                    })
                );
            };

            queueLoad("products", products.length > 0, () => dispatcher(getProductsList()), productStatus);
            queueLoad("categories", categories.length > 0, () => dispatcher(getCategoryList()), categoryStatus);
            queueLoad("floors", floors.length > 0, () => dispatcher(getFloorList()));
            queueLoad("places", places.length > 0, () => dispatcher(getPlaceList()));
            queueLoad("tables", tables.length > 0, () => dispatcher(getTableList()));
            queueLoad("props", props.length > 0, () => dispatcher(getPropsList()));
            queueLoad(
                "clientInfo",
                clientInfoStatus === STATUS.SUCCESS,
                () => dispatcher(getClientInfo({ clientId: AppConfig.getCLientId() || ""})),
                clientInfoStatus
            );

            await Promise.all(requests);
        } catch(e) {
            console.error(e);
        }
    }

    
    useEffect(() => {
         loadAll();
    }, [
        dispatcher,
        products.length,
        productStatus,
        categories.length,
        categoryStatus,
        floors.length,
        places.length,
        tables.length,
        props.length,
        clientInfoStatus,
        clientInfoError,
    ]);


    return {message};
}

export {useStartupLoader};
